// ** React Imports
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// ** Icons
import Play from "mdi-material-ui/Play";

// ** Utils (reused)
import { courseProgress } from "src/@core/utils/course-progress";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";

// Auto-advance interval for the featured rotation.
const ROTATE_MS = 30000;

/******************************************************************
 * ##: FlixHero
 * Netflix-style "destaque"/hero band. Rotates through the featured
 * courses (already ordered by the caller from most recently accessed
 * to oldest), crossfading every ROTATE_MS, with a carousel-style position
 * marker (clickable dots) in the bottom-right when there are 2+ courses.
 * Rotation is disabled for a single course, while the pointer hovers
 * the hero, and when the user prefers reduced motion.
 * @param {Object} props - { courses: Array, loading: boolean }
 * @returns {JSX.Element|null} - the `.flix-hero-rotator`
 * History:
 * 22-07-2026: Created
 * 07-10-2026: Added the bottom-right rotation marker (clickable dots)
 ******************************************************************/
const FlixHero = ({ courses, loading = false }) => {
  const list = useMemo(() => (Array.isArray(courses) ? courses.filter(Boolean) : []), [courses]);
  const count = list.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Keep the active index in range when the list changes.
  useEffect(() => {
    setIndex((i) => (i > count - 1 ? 0 : i));
  }, [count]);

  // Auto-advance. Skipped for a single course, while hovering, or under
  // reduced-motion (then the most recent course simply stays shown).
  useEffect(() => {
    if (count <= 1 || paused || prefersReducedMotion()) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [count, paused]);

  if (loading) {
    return (
      <div className="flix-hero flix-hero--skeleton">
        <div className="flix-hero__inner">
          <span className="flix-skeleton" style={{ width: 320, height: 34 }} />
          <span className="flix-skeleton" style={{ width: 480, maxWidth: "80%", height: 14, marginTop: 18 }} />
          <span className="flix-skeleton" style={{ width: 160, height: 40, marginTop: 24, borderRadius: 6 }} />
        </div>
      </div>
    );
  }

  if (count === 0) return null;

  return (
    <div className="flix-hero-rotator" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {list.map((course, i) => (
        <FlixHeroSlide key={course.course_id ?? i} course={course} active={i === index} />
      ))}

      {/* Carousel-style rotation marker (items + position), bottom-right. Shown
          only with 2+ featured courses; click a dot to jump. Auto-rotation is
          already paused while the pointer is over the hero, so a click settles
          and the 30s timer restarts fresh on mouse-leave. */}
      {count > 1 && (
        <div className="flix-hero-dots" aria-label="Navegação de destaques">
          {list.map((course, i) => (
            <button
              key={course.course_id ?? i}
              type="button"
              className={`flix-hero-dot${i === index ? " is-active" : ""}`}
              aria-label={`Destaque ${i + 1} de ${count}`}
              aria-current={i === index ? "true" : undefined}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/******************************************************************
 * ##: FlixHeroSlide
 * One course "slide" inside the hero rotator: backdrop, ambient glow,
 * title, short description and the resume/start CTA. Only the active
 * slide is visible and interactive; the others are crossfaded out and
 * kept out of the tab order.
 * @param {Object} props - { course, active: boolean }
 * @returns {JSX.Element} - a `.flix-hero-slide`
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const FlixHeroSlide = ({ course, active }) => {
  const location = useLocation();
  const progress = courseProgress(course);
  const to = pathWithPreservedSearch(`/flix/courses/${course.course_id}`, location.search);
  const ctaLabel = progress >= 100 ? "Rever curso" : progress > 0 ? "Continuar a ver" : "Começar agora";
  const hasImage = Boolean(course.image);

  return (
    <div className={`flix-hero-slide${active ? " is-active" : ""}`} aria-hidden={!active}>
      <div className="flix-hero-wrap">
        {hasImage && <div className="flix-hero__glow" style={{ backgroundImage: `url("${course.image}")` }} />}
        <div className="flix-hero">
          <div
            className={`flix-hero__backdrop${hasImage ? "" : " flix-hero__backdrop--empty"}`}
            style={hasImage ? { backgroundImage: `url("${course.image}")` } : undefined}
          />
          <div className="flix-hero__scrim" />
          <div className="flix-hero__inner">
            <span className="flix-hero__eyebrow">Em destaque</span>
            <h1 className="flix-hero__title">{course.title}</h1>
            {course.description && <p className="flix-hero__desc">{stripHtml(course.description)}</p>}
            <div className="flix-hero__actions">
              <Link className="flix-btn flix-btn--primary" to={to} tabIndex={active ? undefined : -1}>
                <Play className="flix-btn__icon" />
                {ctaLabel}
              </Link>
              {progress > 0 && <span className="flix-hero__progress">{progress}% concluído</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/******************************************************************
 * ##: prefersReducedMotion
 * True when the user asked the OS to reduce motion, so the hero should
 * not auto-rotate. Guarded for non-browser / older environments.
 * @returns {Boolean} - whether reduced motion is preferred
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/******************************************************************
 * ##: stripHtml
 * Removes tags from a course description for the plain-text hero
 * blurb (descriptions may contain rich-text markup).
 * @param {String} value - raw description
 * @returns {String} - plain text, trimmed
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const stripHtml = (value) => {
  if (!value || typeof value !== "string") return "";
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

export default FlixHero;
